// import { CircularProgress } from '@mui/material'
import { useState, useMemo, memo } from "react";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";

import ExchangeIcon from "../../imgs/exchange.svg";
import { TradeRoute } from "../../libs/pool";
import {
  getFeeAmount,
  getMinimumReceived,
  getMaximumSold,
  getPriceImpact,
  getAvgPrice,
  getSymbolsFromTradeRoute,
  getRouteStringFromTradeRoute,
} from "../../libs/swap_sdk";
import InfoItem from "../Common/InfoItem";

type Props = {
  isExactInAmount: boolean;
  tradeRoute: TradeRoute | null;
  slippageRate: string;
};

function PriceInfo(props: Props) {
  const { isExactInAmount, tradeRoute, slippageRate } = props;

  const [inSymbol, outSymbol] = useMemo(() => {
    if (!tradeRoute) {
      return ["", ""];
    }
    const symbols = getSymbolsFromTradeRoute(tradeRoute);
    if (!symbols) return ["", ""];
    return symbols;
  }, [tradeRoute]);

  const [priceInfoExpanded, setPriceInfoExpanded] = useState(false);
  const avgPrice = useMemo(() => getAvgPrice(tradeRoute), [tradeRoute]);

  return (
    <div className="bg-primary-200 relative bg-opacity-100 rounded-b-3xl text-white py-2">
      <div className={`px-7 `}>
        <div className="flex justify-between">
          <div className="flex items-center gap-2">
            <img
              src={ExchangeIcon}
              alt="exchange icon"
              className="max-sm:hidden"
            />
            <div className="text-opacity-50 text-white whitespace-nowrap">
              Exchange Rate
            </div>
          </div>
          <div className="flex flex-col justify-center items-end">
            <div className="flex justify-end">
              {/* <CircularProgress size={20} variant="determinate" value={progress} sx={{ mr: 1 }} /> */}
              <span className="text-lg text-white text-opacity-50 font-semibold max-sm:text-sm">
                {tradeRoute
                  ? `1 ${inSymbol} = ${avgPrice} ${outSymbol}`
                  : "No route found"}
              </span>
            </div>
            <div
              className="flex justify-center items-center gap-2 cursor-pointer"
              onClick={() => {
                setPriceInfoExpanded(!priceInfoExpanded);
              }}
            >
              <div className="text-white text-opacity-50 text-xl max-sm:text-sm">
                More Info
              </div>
              {priceInfoExpanded ? <BsChevronUp /> : <BsChevronDown />}
            </div>
          </div>
        </div>
      </div>
      {priceInfoExpanded && (
        <div className="flex top-20 w-full flex-col bg-primary-200 gap-2 rounded-lg py-4 px-7 ">
          <InfoItem
            label="Rate"
            value={
              tradeRoute ? `1 ${inSymbol} = ${avgPrice} ${outSymbol} ` : "--"
            }
          />
          {tradeRoute && tradeRoute.pools.length === 1 && (
            <InfoItem
              label="Currency Reserves"
              value={
                tradeRoute.trade.in_coin < tradeRoute.trade.out_coin
                  ? (
                      Number(tradeRoute.pools[0].reserve_x) /
                      Math.pow(10, tradeRoute.pools[0].x_coin_info.decimals!)
                    ).toFixed(3)
                  : (
                      Number(tradeRoute.pools[0].reserve_y) /
                      Math.pow(10, tradeRoute.pools[0].y_coin_info.decimals!)
                    ).toFixed(3)
              }
            />
          )}
          {tradeRoute && tradeRoute.pools.length === 1 && (
            <InfoItem
              label=""
              value={
                tradeRoute.trade.in_coin < tradeRoute.trade.out_coin
                  ? (
                      Number(tradeRoute.pools[0].reserve_y) /
                      Math.pow(10, tradeRoute.pools[0].y_coin_info.decimals!)
                    ).toFixed(3)
                  : (
                      Number(tradeRoute.pools[0].reserve_x) /
                      Math.pow(10, tradeRoute.pools[0].x_coin_info.decimals!)
                    ).toFixed(3)
              }
            />
          )}
          <InfoItem
            label="Price Impact"
            value={
              tradeRoute
                ? "~ " + getPriceImpact(tradeRoute, Number(avgPrice)) + "%"
                : "--"
            }
          />
          {isExactInAmount ? (
            <InfoItem
              label="Minimum Received"
              value={`${
                tradeRoute
                  ? getMinimumReceived(tradeRoute, Number(slippageRate))
                  : "--"
              } ${outSymbol}`}
            />
          ) : (
            <InfoItem
              label="Maximum Sold"
              value={`${
                tradeRoute
                  ? getMaximumSold(tradeRoute, Number(slippageRate))
                  : "--"
              } ${inSymbol}`}
            />
          )}
          <InfoItem
            label="Fees paid to Foil"
            value={`${
              tradeRoute ? getFeeAmount(tradeRoute).toFixed(4) : "--"
            } ${inSymbol}`}
          />
          {tradeRoute && tradeRoute.pools.length > 1 && (
            <InfoItem
              label="Route"
              value={getRouteStringFromTradeRoute(tradeRoute)}
            />
          )}
          <InfoItem label="Transaction Fee[?]" value="0.00210 APT" />
        </div>
      )}
    </div>
  );
}

export default memo(PriceInfo);
